import { isDevelopmentEnv } from "@/react/helpers/envHelpers";
import { djb2Hash } from "@/react/helpers/hashHelpers";

// Percentage of visitors that will have their signup funnel events tracked.
// Bump gradually (10 -> 25 -> 50 -> 100) while monitoring event volume.
const SAMPLE_PERCENT = 100;

/**
 * Deterministic sampling for member signup funnel tracking events.
 *
 * Uses the ahoy visitor UUID (`window.ANALYTICS_TRACKER_VISITOR`, set
 * server-side before React mounts) to bucket each visitor into 0-99.
 * If their bucket falls below SAMPLE_PERCENT the visitor is sampled.
 *
 * Why visitor ID and not member ID or session random?
 * - Member ID isn't available during the first signup step (account doesn't exist yet).
 * - Session random isn't deterministic across page reloads, so a visitor could
 *   be sampled for step 0 but not step 1 after a refresh.
 * - Visitor ID is available before signup, deterministic across reloads,
 *   and consistent across all funnel steps.
 *
 * In development, sampling is always enabled (100%) for easier debugging.
 */
export const useShouldSampleSignupTracking = (): boolean => {
  if (isDevelopmentEnv()) {
    return true;
  }

  const visitorId = window.ANALYTICS_TRACKER_VISITOR;
  if (!visitorId) {
    return false;
  }

  const bucket = djb2Hash(visitorId) % 100;
  return bucket < SAMPLE_PERCENT;
};
